import {
  ChangeEvent,
  FormEvent,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import * as actions from "../../ducks/auth";
import { useAppDispatch } from "../../redux-store";
import { useTranslation } from "react-i18next";
import { initialize } from "../../ducks/features";
import { appVersion, isMobile } from "../../helpers/mobile";
import {
  ENVIRONMENT_PROD,
  ENVIRONMENT_STAGING,
  getEnvironment,
  setApiEnvironment,
  setOrganizationRef,
} from "../../helpers/apiRequest";
import {
  addOrganization,
  getOrganizations,
} from "../../helpers/organizationStorage";
import Autocomplete from "@mui/material/Autocomplete";
import { Device } from "@capacitor/device";
import { changeLanguage, Languages } from "../../i18n/i18n";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import Logo from "../../styles/logo.svg";
import styled from "styled-components";
import { loginRouteWithLastUsedTrailerForRoute } from "../../ducks/app/route-login";
import { useSendFirebaseToken } from "../../push-notifications/useSendFirebaseToken";
import { errorReturned } from "../../ducks/app/connection";

const NEW_ORGANIZATION_OPTION = {
  reference: "NEW_ORGANIZATION_OPTION",
  name: "Add New",
};

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: -3rem;
  height: calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom));
`;

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("authPage");
  const [offlineError, setOfflineError] = useState(false);
  const [searchParams] = useSearchParams();
  const routeIdRedirectAndLogin = searchParams.get("routeIdRedirectAndLogin");
  // console.log("### SEARCH PARAMS", routeIdRedirectAndLogin);
  const [organization, setOrganization] = useState(
    NEW_ORGANIZATION_OPTION.reference
  );
  const [newOrganization, setNewOrganization] = useState("");
  const [number, setNumber] = useState("");
  const [loginError, setLoginError] = useState<
    null | "AUTH_ERROR" | "SERVER_ERROR"
  >(null);
  const [pin, setPin] = useState("");
  const [landingPageOpen, setLandingPageOpen] = useState(true);
  const [reqInProgress, setReqInProgress] = useState(false);
  const { sendToken: sendFirebaseToken } = useSendFirebaseToken();

  const orgId = searchParams.get("org") ?? undefined;
  const orgName = searchParams.get("orgName") ?? undefined;

  const changeNumber = (e: ChangeEvent<HTMLInputElement>) => {
    setNumber(e.target.value);
  };
  const changePin = (e: ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value);
  };

  useEffect(() => {
    if (isMobile) {
      Device.getLanguageCode().then((code) => {
        changeLanguage(code.value as keyof typeof Languages);
      });
    }
  }, []);

  const seenOrganizations = useMemo(() => {
    if (orgId && orgName) {
      addOrganization({
        name: orgName,
        reference: orgId,
      });
    }
    return [...getOrganizations(), NEW_ORGANIZATION_OPTION];
  }, [orgId, orgName]);

  useEffect(() => {
    // Check if the organization is in the URL and we have seen it before
    const domain = window.location.host.split(".");
    if (domain[1] === "m") {
      const orgRef = domain[0];
      const seenOrg = seenOrganizations.find((org) => org.reference === orgRef);
      if (seenOrg) {
        setOrganization(seenOrg.reference);
      } else {
        setOrganization(seenOrganizations[0].reference);
      }
    } else {
      // Clear the organization ref
      setOrganizationRef(undefined);
      setOrganization(seenOrganizations[0].reference);
    }
  }, [seenOrganizations]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let organizationRef =
      seenOrganizations.find((org) => org.reference === organization)
        ?.reference ?? NEW_ORGANIZATION_OPTION.reference;

    if (organizationRef === NEW_ORGANIZATION_OPTION.reference) {
      organizationRef = newOrganization;
    }

    setOrganizationRef(organizationRef);

    setOfflineError(false);
    if (!navigator.onLine) {
      setOfflineError(true);
      return;
    }

    setReqInProgress(true);
    setLoginError(null);

    dispatch(actions.login({ orgId, number, pin }))
      .unwrap()
      .then((result) => {
        if (result) {
          // Save the organization to local storage
          addOrganization({
            name: result.organisationName,
            reference: organizationRef,
          });
          // upsert the token on every login (the driver can change, thus the BE ties the driver id to the "same" token
          sendFirebaseToken().catch((err) => {
            console.warn("Failed to send push notification token", err);
          });

          const state = location.state as any;
          dispatch(initialize());
          if (routeIdRedirectAndLogin) {
            dispatch(
              loginRouteWithLastUsedTrailerForRoute({
                routeId: routeIdRedirectAndLogin,
              })
            )
              .unwrap()
              .then((drivingUrl) => {
                // console.log("### 22222", drivingUrl);
                navigate(drivingUrl!);
              })
              .catch(() => {
                // Already reported and shown by the thunk; let the driver
                // pick the route manually instead.
                navigate("/routes/login");
              });
            return;
          }
          if (state && state?.returnPath) {
            navigate({
              pathname: state && state?.returnPath,
            });
          } else {
            navigate("/routes/login");
          }
        } else {
          setLoginError("AUTH_ERROR");
        }
      })
      .catch((err) => {
        try {
          const errStringified = JSON.stringify(err);
          alert(errStringified);
        } catch (_e) {
          alert("Error reaching the server: " + err);
        }
        dispatch(errorReturned());
        setLoginError("SERVER_ERROR");
      })
      .finally(() => {
        setReqInProgress(false);
      });
  };

  const [environment, setEnvironment] = useState(getEnvironment());
  const changeEnvTimeout = useRef<NodeJS.Timeout | null>(null);
  const onMouseDown = () => {
    if (!isMobile) {
      return;
    }
    changeEnvTimeout.current = setTimeout(() => {
      const newEnvironment =
        environment === ENVIRONMENT_STAGING
          ? ENVIRONMENT_PROD
          : ENVIRONMENT_STAGING;
      setApiEnvironment(newEnvironment);
      setEnvironment(newEnvironment);
      CapacitorUpdater.setChannel({
        channel: newEnvironment === ENVIRONMENT_STAGING ? "dev" : "production",
        triggerAutoUpdate: true,
      });
    }, 10000);
  };

  const onMouseUp = () => {
    if (changeEnvTimeout.current) {
      clearTimeout(changeEnvTimeout.current);
      changeEnvTimeout.current = null;
    }
  };
  const [invalidInvite] = useState(false);

  const [lastTap, setLastTap] = useState(0);

  const handleDoubleTap = () => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 400; // ms
    if (now - lastTap < DOUBLE_TAP_DELAY) {
      setLandingPageOpen(false);
    }
    setLastTap(now);
  };

  if (isMobile && seenOrganizations.length <= 1 && landingPageOpen) {
    return (
      <Container maxWidth="xs">
        <StyledBox>
          <img
            src={Logo}
            alt=""
            style={{ width: "75%", padding: 10 }}
            onClick={handleDoubleTap}
          />
          <Typography component="h1" variant="h5" textAlign="center" mt={4}>
            {t("login.welcome.clickInvite")}
          </Typography>
          {invalidInvite && (
            <Alert severity="error">{t("login.welcome.invalidInvite")}</Alert>
          )}
        </StyledBox>
      </Container>
    );
  }

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        bgcolor:
          environment === ENVIRONMENT_STAGING ? "var(--warning-200)" : "none",
      }}
    >
      <Box
        mt={14}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div onPointerDown={onMouseDown} onPointerUp={onMouseUp}>
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }} />
        </div>
        <Typography component="h1" variant="h5">
          {t("login.heading")}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {isMobile && (
            <>
              <Autocomplete
                disablePortal
                fullWidth
                options={seenOrganizations}
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    required
                    fullWidth
                    id="organization"
                    label={t("login.textFieldLabel_ORGANIZATION")}
                    name="organization"
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  />
                )}
                value={seenOrganizations.find(
                  (org) => org.reference === organization
                )}
                getOptionLabel={(option) =>
                  typeof option === "object" ? option.name : option
                }
                onChange={(e, value) => {
                  setOrganization(value.reference);
                }}
              />
              {organization === NEW_ORGANIZATION_OPTION.reference && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="newOrganization"
                  label={t("login.textFieldLabel_NEW_ORGANIZATION")}
                  name="newOrganization"
                  value={newOrganization}
                  onChange={(e) => setNewOrganization(e.target.value)}
                />
              )}
            </>
          )}
          <TextField
            margin="normal"
            required
            fullWidth
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 4,
            }}
            InputLabelProps={{ shrink: true }}
            id="drivers_number"
            label={t("login.textFieldLabel_DRIVER")}
            name="drivers_number"
            autoComplete="drivers_number"
            value={number}
            onChange={changeNumber}
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 4,
            }}
            InputLabelProps={{ shrink: true }}
            name="password"
            label={t("login.textFieldLabel_PIN")}
            value={pin}
            onChange={changePin}
            type="password"
            id="password"
            autoComplete="current-password"
          />
          {loginError && (
            <Alert severity="error">
              {loginError === "AUTH_ERROR" ? (
                <span>{t("login.errorMessage")}</span>
              ) : (
                <span>{t("login.errorMessage_GENERAL")}</span>
              )}
            </Alert>
          )}
          {offlineError && <Alert severity="error">{t("offlineError")}</Alert>}
          <Button
            loading={reqInProgress}
            type="submit"
            fullWidth
            size="large"
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            {t("login.loginButtonLabel")}
          </Button>
          {appVersion && (
            <Typography variant="caption" sx={{ textAlign: "center" }}>
              Version {appVersion}
            </Typography>
          )}
        </Box>
      </Box>
      {environment === ENVIRONMENT_STAGING && (
        <Box sx={{ p: 2 }}>
          <Typography>{t("login.stagingWarning")}</Typography>
        </Box>
      )}
    </Container>
  );
}
